import { computed, h, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { NMenu, NPopover } from 'naive-ui'
import { SvgIcon } from '@/components/common'
import { useAppStore } from '@/store'
import { useBasicLayout } from '@/hooks/useBasicLayout'
import { getActiveMenuKey } from '@/utils/routeHelper'
import { t } from '@/locales'

export function useToolsPopover() {
  const router = useRouter()
  const route = useRoute()
  const appStore = useAppStore()
  const { isMobile } = useBasicLayout()

  // Refs để control popover visibility
  const collapsedPopoverRef = ref(null)
  const expandedPopoverRef = ref(null)

  // Computed để kiểm tra trạng thái hiện tại
  const isCollapsed = computed(() => appStore.siderCollapsed)
  const isExpanded = computed(() => !appStore.siderCollapsed)

  // Danh sách tools theo groups
  const toolsGroups = computed(() => [
    {
      title: t('menu.group.image'),
      tools: [
        { label: t('menu.imageGeneration'), icon: 'ri:multi-image-line', key: 'image-generation' },
      ],
    },
    {
      title: t('menu.group.text'),
      tools: [
        { label: t('menu.aiTranslator'), icon: 'ri:translate-2', key: 'ai-translator' },
        { label: t('menu.imageTranslator'), icon: 'ri:image-2-line', key: 'image-translator' },
      ],
    },
    {
      title: t('menu.group.video'),
      tools: [
        { label: t('menu.videoSummarizer'), icon: 'ri:video-line', key: 'video-summarizer' },
      ],
    },
    {
      title: t('menu.group.document'),
      tools: [
        { label: t('menu.mindmap'), icon: 'ri:mind-map', key: 'mind-map' },
        { label: t('menu.deepResearch'), icon: 'ri:search-2-line', key: 'deep-research' },
      ],
    },
    {
      title: t('menu.group.ocr'),
      tools: [
        { label: t('menu.imageToWord'), icon: 'ri:image-line', key: 'image-to-word' },
        { label: t('menu.pdfToWord'), icon: 'ri:file-pdf-2-line', key: 'pdf-to-word' },
      ],
    },
  ])

  // Flatten list cho collapsed menu
  const toolsList = computed(() => toolsGroups.value.flatMap(group => group.tools))

  // Get current active tool key
  const currentToolKey = computed(() => {
    const activeKey = getActiveMenuKey(route)
    if (activeKey) {
      const allToolKeys = toolsList.value.map(tool => tool.key)
      return allToolKeys.includes(activeKey) ? activeKey : null
    }
    return null
  })

  // Handle menu item click
  const handleMenuClick = (key: string, state: 'collapsed' | 'expanded') => {
    router.push({ name: key })
    if (isMobile.value)
      appStore.setSiderCollapsed(true)

    // Đóng popover sau khi click bằng cách setShow(false)
    setTimeout(() => {
      if (state === 'collapsed' && collapsedPopoverRef.value)
        collapsedPopoverRef.value.setShow(false)
      else if (state === 'expanded' && expandedPopoverRef.value)
        expandedPopoverRef.value.setShow(false)
    }, 0)
  }

  // Tạo popover content
  const createPopoverContent = (state: 'collapsed' | 'expanded') => h('div', {
    class: 'min-w-[400px]',
  }, toolsGroups.value.map((group) => {
    // Chia tools thành các hàng 3 cột
    const toolRows = []
    for (let i = 0; i < group.tools.length; i += 3)
      toolRows.push(group.tools.slice(i, i + 3))

    return h('div', {
      class: 'mb-4 last:mb-0',
    }, [
      // Group title
      h('div', {
        class: 'tool-group-title font-bold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600 pb-1 mb-2',
      }, group.title),
      // Group tools using NMenu trong layout 3 cột
      ...toolRows.map(row =>
        h('div', { class: 'grid grid-cols-3 gap-1 mb-1' }, row.map(tool =>
          h('div', { class: 'popover-menu-wrapper' }, [
            h(NMenu, {
              options: [{
                label: tool.label,
                icon: () => h(SvgIcon, { icon: tool.icon, size: '20' }),
                key: tool.key,
              }],
              value: currentToolKey.value === tool.key ? tool.key : null,
              collapsed: false,
              indent: 8,
              iconSize: 20,
              onUpdateValue: (key: string) => handleMenuClick(key, state),
            }),
          ]),
        )),
      ),
    ])
  }))

  // Tạo menu item cho collapsed state
  const getCollapsedToolsMenuItem = () => ({
    label: () => h(NPopover, {
      'ref': collapsedPopoverRef,
      'trigger': 'hover',
      'placement': 'right',
      'disabled': !isCollapsed.value,
      'show-arrow': true,
      'duration': 200,
    }, {
      trigger: () => h('div', {
        class: 'absolute inset-0 w-full h-full cursor-pointer',
      }),
      default: () => createPopoverContent('collapsed'),
    }),
    icon: () => h(SvgIcon, { icon: 'ri:list-view', size: '20' }),
    key: 'collapsed-more-tools',
  })

  // Tạo menu item cho expanded state
  const getExpandedToolsMenuItem = () => ({
    label: () => h(NPopover, {
      'ref': expandedPopoverRef,
      'trigger': 'hover',
      'placement': 'right',
      'disabled': !isExpanded.value,
      'show-arrow': true,
      'duration': 200,
    }, {
      trigger: () => h('div', {
        class: 'absolute inset-0 w-full h-full flex items-center justify-between cursor-pointer',
        style: 'padding-left: 16px; padding-right: 16px;',
      }, [
        h('div', { class: 'flex items-center gap-2' }, [
          h(SvgIcon, { icon: 'ri:list-view', size: '20' }),
          h('span', {}, t('menu.moreTools')),
        ]),
        h(SvgIcon, { icon: 'ri:arrow-right-s-line', size: '20', class: 'text-gray-700 dark:text-gray-300' }),
      ]),
      default: () => createPopoverContent('expanded'),
    }),
    key: 'expanded-more-tools',
  })

  return {
    toolsGroups,
    toolsList,
    getCollapsedToolsMenuItem,
    getExpandedToolsMenuItem,
  }
}
